import type { ButtonHTMLAttributes, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'

interface ToolbarActionButtonProps {
  icon: LucideIcon
  label: string
  onClick: () => void
  /** Accessible name when it must say more than the label (e.g. that something new is waiting). */
  ariaLabel?: string
  /** A small marker drawn on the button's corner (e.g. an unread dot). */
  badge?: ReactNode
  /** Draws only the icon at every width; the label stays as the accessible name. */
  iconOnly?: boolean
  /** State and relationship attributes of a button that opens, folds or toggles something. */
  aria?: Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-expanded' | 'aria-controls' | 'aria-pressed'>
  id?: string
  /** Native tooltip. */
  title?: string
}

export function ToolbarActionButton({ icon: Icon, label, onClick, ariaLabel, badge, iconOnly = false, aria, id, title }: ToolbarActionButtonProps) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="relative h-7 gap-1.5 px-2 text-toolbar-foreground/70 hover:text-toolbar-foreground hover:bg-toolbar-foreground/5"
      id={id}
      title={title}
      aria-label={ariaLabel ?? label}
      onClick={onClick}
      {...aria}
    >
      <Icon className="h-3.5 w-3.5" />
      {!iconOnly && <span className="hidden lg:inline text-xs">{label}</span>}
      {badge}
    </Button>
  )
}
